import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

type SortKey = 'latest' | 'hot' | 'rating';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新' },
  { key: 'hot', label: '最热' },
  { key: 'rating', label: '评分' },
];

const PAGE_SIZE = 6;

export default function Category() {
  usePageMeta({
    title: '河北彩花影视作品分类 - 影视作品详情一览',
    description:
      '河北彩花影视作品全集，涵盖电影与电视剧，可按类型、年份区间与上映地区筛选，并按最新、最热、评分排序查看作品详情。',
    keywords: '河北彩花影视作品,河北彩花电影,河北彩花电视剧,影视作品分类',
  });

  const [searchParams] = useSearchParams();
  const [type, setType] = useState<'全部' | '电影' | '电视剧'>('全部');
  const [yearFrom, setYearFrom] = useState('不限');
  const [yearTo, setYearTo] = useState('不限');
  const [region, setRegion] = useState('全部');
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setKeyword(q);
  }, [searchParams]);

  const yearOptions = useMemo(
    () => Array.from(new Set(movies.map((movie) => movie.year))).sort((a, b) => b - a),
    [],
  );
  const regionOptions = useMemo(() => Array.from(new Set(movies.map((movie) => movie.region))), []);

  const filtered = useMemo(() => {
    let list = movies.filter((movie) => {
      if (type !== '全部' && movie.type !== type) return false;
      if (region !== '全部' && movie.region !== region) return false;
      if (yearFrom !== '不限' && movie.year < Number(yearFrom)) return false;
      if (yearTo !== '不限' && movie.year > Number(yearTo)) return false;
      if (keyword.trim()) {
        const haystack = [movie.title, ...movie.genres, ...movie.cast, String(movie.year)].join(' ').toLowerCase();
        if (!haystack.includes(keyword.trim().toLowerCase())) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      if (sortKey === 'hot') return b.hot - a.hot;
      if (sortKey === 'rating') return b.rating - a.rating;
      return b.releaseDate.localeCompare(a.releaseDate);
    });

    return list;
  }, [type, region, yearFrom, yearTo, sortKey, keyword]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, region, yearFrom, yearTo, sortKey, keyword]);

  const shown = filtered.slice(0, visible);

  const reset = () => {
    setType('全部');
    setYearFrom('不限');
    setYearTo('不限');
    setRegion('全部');
    setSortKey('latest');
    setKeyword('');
  };

  const filterPanel = (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground-900">作品类型</h3>
        <div className="flex flex-wrap gap-2">
          {(['全部', '电影', '电视剧'] as const).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setType(item)}
              className={`rounded-md px-3.5 py-1.5 text-sm transition-colors ${
                type === item
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground-900">年份区间</h3>
        <div className="flex items-center gap-2">
          <select
            value={yearFrom}
            onChange={(event) => setYearFrom(event.target.value)}
            className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          >
            <option value="不限">不限</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}年
              </option>
            ))}
          </select>
          <span className="text-foreground-400">—</span>
          <select
            value={yearTo}
            onChange={(event) => setYearTo(event.target.value)}
            className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          >
            <option value="不限">不限</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}年
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground-900">上映地区</h3>
        <div className="flex flex-wrap gap-2">
          {['全部', ...regionOptions].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setRegion(item)}
              className={`rounded-md px-3.5 py-1.5 text-sm transition-colors ${
                region === item
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={reset}
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
      >
        <i className="ri-refresh-line"></i>
        重置筛选
      </button>
    </div>
  );

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视作品' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            河北彩花影视作品分类 - 影视作品详情一览
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            河北彩花影视作品全集，涵盖电影与电视剧。可按作品类型、年份区间与上映地区筛选，并按最新、最热或评分排序查看。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex gap-8">
          <aside className="hidden w-[240px] shrink-0 lg:block">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">{filterPanel}</div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
                >
                  <i className="ri-filter-3-line"></i>
                  筛选
                </button>
                <p className="text-sm text-foreground-500">
                  共 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
                </p>
              </div>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSortKey(option.key)}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                      sortKey === option.key
                        ? 'bg-background-50 text-primary-700'
                        : 'text-foreground-500 hover:text-foreground-900'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {keyword && (
              <div className="mb-4 flex items-center gap-2 rounded-md bg-primary-50 px-4 py-2.5 text-sm text-primary-700">
                <i className="ri-search-line"></i>
                当前筛选关键词：“{keyword}”
                <button
                  type="button"
                  onClick={() => setKeyword('')}
                  className="ml-auto inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800"
                >
                  清除
                </button>
              </div>
            )}

            {shown.length > 0 ? (
              <>
                <div className="flex flex-col gap-4">
                  {shown.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} variant="list" />
                  ))}
                </div>

                {visible < filtered.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-add-line"></i>
                      加载更多（剩余 {filtered.length - visible} 部）
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="mt-4 text-base font-medium text-foreground-800">没有找到符合条件的内容</p>
                <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后再看看</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setDrawerOpen(false)}
            role="presentation"
          ></div>
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-[340px] overflow-y-auto bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {filterPanel}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      )}
    </div>
  );
}